import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Gift, Heart } from 'lucide-react';

export interface GiftDetails {
  tier: 'premium_economy' | 'first_class';
  duration: string;
  price: string;
}

export interface GiftForm {
  recipientName: string;
  recipientEmail: string;
  senderName: string;
  message: string;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  giftDetails: GiftDetails | null;
  setGiftDetails: (d: GiftDetails) => void;
  giftForm: GiftForm;
  setGiftForm: (f: GiftForm) => void;
  loading: boolean;
  onSubmit: () => void;
}

export const GiftModal = ({
  open, onOpenChange, giftDetails, setGiftDetails, giftForm, setGiftForm, loading, onSubmit,
}: Props) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-xl">
          <Gift className="h-5 w-5 text-primary" />
          Send a Gift Subscription
        </DialogTitle>
        <DialogDescription>Share the gift of seamless travel planning</DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {giftDetails && (
          <div className="space-y-2">
            <Label htmlFor="duration">Select Duration *</Label>
            <Select
              value={`${giftDetails.tier}|${giftDetails.duration}`}
              onValueChange={(value) => {
                const [tier, duration] = value.split('|');
                const prices = {
                  'premium_economy|1 Month': '€6.99',
                  'premium_economy|3 Months': '€19.50',
                  'premium_economy|6 Months': '€36.49',
                  'premium_economy|Annual': '€67.10',
                  'first_class|1 Month': '€11.99',
                  'first_class|3 Months': '€33.45',
                  'first_class|6 Months': '€62.59',
                  'first_class|Annual': '€115.10',
                };
                setGiftDetails({
                  tier: tier as 'premium_economy' | 'first_class',
                  duration,
                  price: prices[value as keyof typeof prices],
                });
              }}
            >
              <SelectTrigger className="w-full bg-background"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card/95 backdrop-blur-md border border-primary/20 z-50">
                <SelectItem value={`${giftDetails.tier}|1 Month`}>
                  1 Month - {giftDetails.tier === 'premium_economy' ? '€6.99' : '€11.99'}
                </SelectItem>
                <SelectItem value={`${giftDetails.tier}|3 Months`}>
                  3 Months - {giftDetails.tier === 'premium_economy' ? '€19.50' : '€33.45'} (Save 7%)
                </SelectItem>
                <SelectItem value={`${giftDetails.tier}|6 Months`}>
                  6 Months - {giftDetails.tier === 'premium_economy' ? '€36.49' : '€62.59'} (Save 13%)
                </SelectItem>
                <SelectItem value={`${giftDetails.tier}|Annual`}>
                  Annual - {giftDetails.tier === 'premium_economy' ? '€67.10' : '€115.10'} (Save 20%)
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="mt-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-sm font-semibold text-foreground">
                {giftDetails.tier === 'premium_economy' ? 'Premium Economy Pack' : 'First Class Pack'}
              </p>
              <p className="text-lg font-bold text-primary">{giftDetails.price}</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="senderName">Your Name *</Label>
          <Input id="senderName" placeholder="John Doe" value={giftForm.senderName} onChange={(e) => setGiftForm({ ...giftForm, senderName: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="recipientName">Recipient's Name *</Label>
          <Input id="recipientName" placeholder="Jane Smith" value={giftForm.recipientName} onChange={(e) => setGiftForm({ ...giftForm, recipientName: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="recipientEmail">Recipient's Email *</Label>
          <Input id="recipientEmail" type="email" placeholder="jane@example.com" value={giftForm.recipientEmail} onChange={(e) => setGiftForm({ ...giftForm, recipientEmail: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="message">Personal Message (Optional)</Label>
          <Textarea id="message" placeholder="Happy travels! I hope this helps..." value={giftForm.message} onChange={(e) => setGiftForm({ ...giftForm, message: e.target.value })} rows={3} />
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1" disabled={loading}>Cancel</Button>
        <Button onClick={onSubmit} className="flex-1 bg-gradient-to-r from-primary to-accent text-white">
          <Heart className="h-4 w-4 mr-2" />
          Continue to Checkout
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
